import { Dispatch, useEffect } from 'react';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../common/constants';
import { useArrowKeyIndex } from '../../../common/hooks/keyboard/useArrowKeyIndex';
import { useEnterKeyHandler } from '../../../common/hooks/keyboard/useEnterKeyHandler';
import { useEscapeKeyHandler } from '../../../common/hooks/keyboard/useEscapeKeyHandler';
import { useDebouncedValue } from '../../../common/hooks/useDebounceValue';
import { useHannibalQuery } from '../../../common/schema/hannibal/HannibalContext';
import { GeoLocationAutocompleteDocument } from '../../../common/schema/hannibal/types.generated';
import {
  AutoCompleteDropdown,
  MiniLoadingSpinner,
  SuburbLoadingWrapper,
} from './LocationBox.styles';
import { LocationBoxSuggestion } from './LocationBoxSuggestion';
import { SuburbType } from './SuburbType';

interface Props {
  query: string;
  onSelect: Dispatch<SuburbType>;
  cancel: () => void;
}

export const AutoCompleteBody = ({ query, onSelect, cancel }: Props) => {
  const debouncedQuery = useDebouncedValue(query, FAST_USER_INTERACTION_DEBOUNCE);

  const { data, loading } = useHannibalQuery(GeoLocationAutocompleteDocument, {
    variables: { query: debouncedQuery },
  });

  const suburbs = data?.searchSuburbsByName.suburbs ?? [];
  const [selectedIndex, setSelectedIndex] = useArrowKeyIndex(suburbs.length);

  useEnterKeyHandler(() => {
    const suburb = suburbs[selectedIndex];
    if (suburb) onSelect(suburb);
  });

  useEscapeKeyHandler(cancel);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, setSelectedIndex]);

  return (
    <>
      {loading && (
        <SuburbLoadingWrapper data-testid="suburbs-loading">
          <MiniLoadingSpinner />
        </SuburbLoadingWrapper>
      )}

      {!loading && (
        <AutoCompleteDropdown>
          {suburbs.map((suburb, index) => (
            <LocationBoxSuggestion
              key={index}
              suburb={suburb}
              onChoose={onSelect}
              onHover={setSelectedIndex}
              isSelected={index === selectedIndex}
              index={index}
            />
          ))}
        </AutoCompleteDropdown>
      )}
    </>
  );
};
